import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { MapSettingsTab } from './MapSettingsTab';
import { Alert, Box, Card, CardContent, Chip, Divider, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError, uploadFile } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { fetchOrgSettings } from '../lib/org-brand';
import { Button, FileButton } from './button';
import { Loading } from './common';
import { InfoRow } from './org';
import { OrgLogo } from './org-logo';
import { ORG_SOCIAL_PLATFORM_LABELS, ORG_SOCIAL_PLATFORMS, type OrgSocialPlatform } from '@re/shared';
import { getUiLanguage, localizedOrganizationName, ORG_SOCIAL_PLATFORM_LABELS_EN, tr } from '../lib/i18n';
import { OrgSocialPlatformIcon } from './org-social-icons';
import { PageActions } from './page-actions';
import { TextField } from './text-field';
import { useToast } from './Toast';

const HEX = /^#[0-9A-Fa-f]{6}$/;
/** Same rule as the API: white text must stay readable on the brand color. */
const MIN_CONTRAST = 2.5;
function contrastWithWhite(hex: string): number {
  const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 1.05 / (0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]! + 0.05);
}
const PLACEHOLDER: Record<OrgSocialPlatform, string> = {
  website: 'https://example.com',
  instagram: 'https://instagram.com/…',
  facebook: 'https://facebook.com/…',
  twitter: 'https://x.com/…',
  tiktok: 'https://tiktok.com/@…',
};
type Links = Record<OrgSocialPlatform, string>;
const linksOf = (list: { platform: OrgSocialPlatform; url: string }[]): Links =>
  Object.fromEntries(ORG_SOCIAL_PLATFORMS.map((p) => [p, list.find((l) => l.platform === p)?.url ?? ''])) as Links;
const sameLinks = (a: Links, b: Links) => ORG_SOCIAL_PLATFORMS.every((p) => a[p].trim() === b[p].trim());

export function SettingsPage() {
  const { reload } = useAuth();
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const q = useQuery({ queryKey: ['settings'], queryFn: fetchOrgSettings });
  const form = useQuery({ queryKey: ['form'], queryFn: () => api<{ version: number }>('/form'), staleTime: 60_000 });
  const [tab, setTab] = useState<'general' | 'brand' | 'social' | 'map'>('general');
  const [phone, setPhone] = useState('');
  const [timezone, setTimezone] = useState('');
  const [color, setColor] = useState('');
  const [links, setLinks] = useState<Links>(linksOf([]));
  const [linkErrors, setLinkErrors] = useState<Partial<Record<OrgSocialPlatform, string>>>({});
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (q.data) {
      setPhone(q.data.phone ?? '');
      setTimezone(q.data.timezone);
      setColor(q.data.primaryColor ?? '');
      setLinks(linksOf(q.data.socialLinks));
      setLinkErrors({});
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api('/settings', { method: 'PATCH', body }),
    onSuccess: () => { toast(tr('تم حفظ الإعدادات', 'Settings saved')); void qc.invalidateQueries({ queryKey: ['settings'] }); void reload(); },
    onError: (e) => {
      // map "socialLinks.N.url" errors back to the platform field
      if (e instanceof ApiError) {
        const sent = ORG_SOCIAL_PLATFORMS.filter((p) => links[p].trim());
        const errs: Partial<Record<OrgSocialPlatform, string>> = {};
        for (const [f, m] of Object.entries(e.fieldErrors())) {
          const mm = /^socialLinks\.(\d+)\.url$/.exec(f);
          const p = mm ? sent[Number(mm[1])] : undefined;
          if (p) errs[p] = m;
        }
        setLinkErrors(errs);
      }
      toastError(e);
    },
  });

  const onLogo = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    try {
      const id = await uploadFile(f);
      await save.mutateAsync({ logoFileId: id });
    } catch (e) {
      toastError(e);
    } finally {
      setUploading(false);
    }
  };

  const s = q.data;
  const organizationName = localizedOrganizationName(s);
  const colorOk = color === '' || (HEX.test(color) && contrastWithWhite(color) >= MIN_CONTRAST);
  const colorMsg = color === '' ? tr('بدون لون — يُستخدم لون النظام', 'No color — the system default is used') : !HEX.test(color) ? tr('صيغة اللون: #RRGGBB', 'Color format: #RRGGBB') : !colorOk ? tr('اللون فاتح جداً — النص الأبيض عليه لن يُقرأ. اختر لوناً أغمق', 'This color is too light for white text. Choose a darker color.') : tr('يظهر في لوحتكم وفي النموذج العام لعملائكم', 'Used in your dashboard and public form');
  const colorDirty = !!s && color !== (s.primaryColor ?? '');
  const linksDirty = !!s && !sameLinks(links, linksOf(s.socialLinks));
  const dirty = !!s && (phone !== (s.phone ?? '') || timezone !== s.timezone || colorDirty || linksDirty);
  const saveAll = () => {
    if (!s) return;
    save.mutate({
      ...(phone !== (s.phone ?? '') && phone.trim() ? { phone: phone.trim() } : {}),
      ...(timezone !== s.timezone ? { timezone: timezone.trim() } : {}),
      ...(colorDirty ? { primaryColor: color || null } : {}),
      ...(linksDirty ? { socialLinks: ORG_SOCIAL_PLATFORMS.filter((p) => links[p].trim()).map((p) => ({ platform: p, url: links[p].trim() })) } : {}),
    });
  };

  if (q.isLoading) return <Loading />;
  if (q.isError || !s) return <Alert severity="error">{tr('تعذر تحميل الإعدادات.', 'Unable to load settings.')}</Alert>;

  return (
    <>
      {s.canEdit && dirty && (
        <PageActions>
          <Button variant="contained" disabled={uploading || !colorOk} loading={save.isPending && !uploading} onClick={saveAll}>
            {tr('حفظ التغييرات', 'Save changes')}
          </Button>
        </PageActions>
      )}
      <Card sx={{ width: '100%', maxWidth: 1000, mx: 'auto', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider', bgcolor: (theme) => theme.palette.m3.surfaceContainerLow }}>
          <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" aria-label={tr('أقسام إعدادات الشركة', 'Organization settings sections')}>
            <Tab value="general" icon={<BusinessOutlinedIcon />} iconPosition="start" label={tr('عام', 'General')} />
            <Tab value="brand" icon={<PaletteOutlinedIcon />} iconPosition="start" label={tr('الهوية', 'Branding')} />
            <Tab value="social" icon={<ShareOutlinedIcon />} iconPosition="start" label={tr('التواصل', 'Social links')} />
            <Tab value="map" icon={<MapOutlinedIcon />} iconPosition="start" label={tr('الخريطة', 'Map')} />
          </Tabs>
        </Box>
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: { xs: 2, md: 3 }, '&:last-child': { pb: 3 } }}>
          <Box sx={{ maxWidth: 720, mx: 'auto' }}>
            {tab === 'general' && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">{tr('معلومات الشركة', 'Organization information')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tr('البيانات الأساسية المستخدمة داخل النظام وفي النموذج العام.', 'Basic information used in the system and public form.')}</Typography>
                </Box>
                <Divider />
                <InfoRow label={tr('اسم الشركة', 'Organization name')}>{organizationName}</InfoRow>
                <InfoRow label={tr('رمز الشركة', 'Organization code')}><span dir="ltr">{s.slug}</span></InfoRow>
                <InfoRow label={tr('البريد', 'Email')}>{s.email ?? '—'}</InfoRow>
                <InfoRow label={tr('الرابط العام', 'Public link')}><Chip size="small" label={s.public_form_enabled ? tr('مفعّل', 'Enabled') : tr('معطّل', 'Disabled')} color={s.public_form_enabled ? 'success' : 'default'} /></InfoRow>
                <InfoRow label={tr('نسخة النموذج', 'Form version')}>{form.data ? `v${form.data.version}` : '—'}</InfoRow>
                <Divider />
                <Typography variant="subtitle1">{tr('معلومات التواصل والتوقيت', 'Contact and time zone')}</Typography>
                {save.isError && <Alert severity="error">{save.error instanceof Error ? save.error.message : tr('تعذر حفظ الإعدادات', 'Unable to save settings')}</Alert>}
                <TextField fullWidth label={tr('هاتف الشركة', 'Organization phone')} value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!s.canEdit} helperText={tr('يظهر للعملاء في النموذج العام', 'Shown to customers in the public form')} slotProps={{ htmlInput: { dir: 'ltr' } }} />
                <TextField fullWidth label={tr('المنطقة الزمنية', 'Time zone')} value={timezone} onChange={(e) => setTimezone(e.target.value)} disabled={!s.canEdit} helperText={tr('مثال: Asia/Amman', 'Example: Asia/Amman')} slotProps={{ htmlInput: { dir: 'ltr' } }} />
              </Stack>
            )}

            {tab === 'brand' && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6">{tr('هوية الشركة', 'Organization branding')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tr('الشعار واللون يظهران في لوحة الشركة والنموذج العام.', 'The logo and color appear in the dashboard and public form.')}</Typography>
                </Box>
                <Divider />
            <Stack alignItems="center" spacing={1}>
              <OrgLogo src={s.logoUrl} variant="rounded" sx={{ width: 72, height: 72, fontSize: 28 }}>{organizationName[0]}</OrgLogo>
              <Typography variant="h6">{organizationName}</Typography>
              {s.canEdit && (
                <FileButton
                  size="small"
                  startIcon={<PhotoCameraIcon />}
                  loading={uploading}
                  inputProps={{
                    accept: 'image/png,image/jpeg,image/webp',
                    onChange: (e) => {
                      void onLogo(e.target.files?.[0]);
                      e.target.value = '';
                    },
                  }}
                >
                  {s.logoUrl ? tr('تغيير الشعار', 'Change logo') : tr('رفع شعار', 'Upload logo')}
                </FileButton>
              )}
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <TextField
                label={tr('اللون الأساسي', 'Primary color')}
                value={color}
                onChange={(e) => setColor(e.target.value.trim())}
                disabled={!s.canEdit}
                error={!colorOk}
                helperText={colorMsg}
                placeholder="#0B57D0"
                sx={{ flex: 1 }}
                slotProps={{ htmlInput: { dir: 'ltr', maxLength: 7 } }}
              />
              <TextField
                type="color"
                label=" "
                value={HEX.test(color) ? color : '#0B57D0'}
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                disabled={!s.canEdit}
                sx={{ width: 72, '& input': { height: 40, cursor: 'pointer' } }}
              />
            </Stack>
              </Stack>
            )}

            {tab === 'map' && <MapSettingsTab value={s.map} countryId={s.default_country_id} canEdit={s.canEdit} />}

            {tab === 'social' && (
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h6">{tr('روابط التواصل', 'Social links')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tr('تظهر لعملائكم في النموذج العام. اترك أي حقل فارغاً لإخفائه.', 'These links appear in the public form. Leave a field empty to hide it.')}</Typography>
                </Box>
                <Divider />
                {ORG_SOCIAL_PLATFORMS.map((p) => (
                  <Stack key={p} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120, pt: { sm: 2 } }}>
                      <OrgSocialPlatformIcon platform={p} fontSize="small" color="action" />
                      <Typography variant="body2">{(getUiLanguage() === 'en' ? ORG_SOCIAL_PLATFORM_LABELS_EN : ORG_SOCIAL_PLATFORM_LABELS)[p]}</Typography>
                    </Stack>
                    <TextField
                      fullWidth
                      size="small"
                      value={links[p]}
                      onChange={(e) => { setLinks((l) => ({ ...l, [p]: e.target.value })); setLinkErrors((x) => ({ ...x, [p]: undefined })); }}
                      disabled={!s.canEdit}
                      placeholder={PLACEHOLDER[p]}
                      error={!!linkErrors[p]}
                      helperText={linkErrors[p]}
                      slotProps={{ htmlInput: { dir: 'ltr' } }}
                    />
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
